import { useEffect, useRef } from 'react';

/**
 * Canonical registry of the app's cross-feature `window` CustomEvent names.
 *
 * These events are how one surface tells another that shared delivery data
 * changed, so the second surface re-fetches without a manual refresh (mirror of
 * the pattern in {@link useDataRefresh}). Before this module the names were
 * inline string literals scattered across dispatchers and listeners — a single
 * typo silently broke reactivity with no compile error. Import the constant
 * instead of typing the string, and `emitAppEvent` / `useAppEvents` keep both
 * ends in sync.
 *
 * Adding an event? Put its name here and reference the constant everywhere —
 * never re-type the literal.
 */
export const AppEvent = {
  /** Demo data was seeded (e.g. the guided tour's first run). */
  DemoDataLoaded: 'avium:demo-data-loaded',
  /** Demo data was removed. */
  DemoDataDeleted: 'demo-data-deleted',
  /** An admin changed a status→phase mapping; boards must re-shape. */
  StatusMappingChanged: 'avium:status-mapping-changed',
  /** A ticket moved to a different phase (e.g. into Done) — flow gauges restale. */
  TicketPhaseChanged: 'avium:ticket-phase-changed',
  /** The count of locally-edited (out-of-sync) tickets changed. */
  JiraModifiedCountChanged: 'jira-modified-count-changed',
  /** Any external source sync finished (Jira / Azure DevOps / Asana). */
  JiraSynced: 'jira-synced',
  /** A Doc→Stories import created new work. */
  TicketsCreated: 'avium:tickets-created',
  /** The plan itself changed — a move landed, a scenario was made real, or a
   *  ticket's assignee/sprint/estimate was edited. THE data signal every
   *  cross-surface refresh (Brief, Dashboard, KG) subscribes to; distinct from
   *  `JiraModifiedCountChanged`, which only drives the push-queue badge count. */
  PlanChanged: 'avium:plan-changed',
} as const;

export type AppEventName = (typeof AppEvent)[keyof typeof AppEvent];

/** Dispatch a cross-feature app event. */
export function emitAppEvent(name: AppEventName): void {
  window.dispatchEvent(new CustomEvent(name));
}

/**
 * Subscribe one handler to one or more app events for the component's lifetime.
 * The handler is always invoked with its latest closure (kept in a ref), so you
 * may pass an inline callback without re-subscribing on every render. The
 * `events` list is read once on mount — keep it static (a module-level array or
 * an inline literal), not a value that changes between renders.
 */
export function useAppEvents(events: readonly AppEventName[], handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const listener = () => handlerRef.current();
    for (const evt of events) window.addEventListener(evt, listener);
    return () => {
      for (const evt of events) window.removeEventListener(evt, listener);
    };
    // `events` is required to be static; subscribe once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
